import { mm } from "@inovitas/infra3dsdk";
import { ChevronLeft, Delete } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import getChangedFeaturesList from "shared/common/utils/layers/getChangedFeaturesList";
import SingleFeatureTable from "shared/ui/toolbar/tools/layertreeAndEdit/featureTable/SingleFeatureTable";

interface props {
  rows: mm.Feature[];
}

export default function EditFeatureList({ rows }: props) {
  const { t } = useTranslation();

  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);

  const editToolActivated = sidebarStore((state) => state.editToolActivated);
  const selectedTableRows = sidebarStore((state) => state.selectedTableRows);
  const changeSelectedTableRows = sidebarStore(
    (state) => state.changeSelectedTableRows
  );
  const editPopover = sidebarStore((state) => state.editPopover);
  const changeFeatureTableAddedRows = sidebarStore(
    (state) => state.changeFeatureTableAddedRows
  );

  //select feature function
  const selectFeature = (feature: mm.Feature) => {
    if (
      selectedTableRows &&
      feature.toGeoJsonString() === JSON.stringify(selectedTableRows)
    ) {
      changeSelectedTableRows(undefined);
    } else {
      changeSelectedTableRows(feature.toGeoJSON());
    }
  };

  //delete feature function
  const deleteFeature = (item: mm.Feature) => {
    if (viewer == undefined) return;
    const layer = viewer.getScene().getLayerHandler().getActiveLayer();
    if (layer === null) return;
    if (editToolActivated === "add") {
      layer.getLayerEditor().featuresinsert.destroyFeature(item, true);
    } else if (editToolActivated === "edit") {
      let old_feature;
      let rowsCount = 0;
      layer.exportFeatures(
        (featuresLength) => {
          rowsCount = featuresLength;
          return {};
        },
        (error) => {
          console.error("error", error);
          return {};
        },
        {
          countonly: true,
        }
      );
      layer.exportFeatures(
        (geojson) => {
          old_feature = geojson.features.find(
            (feature: mm.Feature) => feature.id == item.id
          );
          return {};
        },
        (err: string) => {
          console.error(err);
          return {};
        },
        {
          start: 0,
          limit: rowsCount,
          srid: layer.scene.getCurrentGeoframe().srid,
        }
      );

      layer
        .getSystempropertyHandler()
        .handleSystemProperties(old_feature, "onedit");
      layer.getLayerEditor().updateFeatures(old_feature);
      delete layer.getLayerEditor().featuresupdate[item.id];
      layer.featureselector.clearSelection();
      layer.reload();
    }
    changeFeatureTableAddedRows(getChangedFeaturesList(layer));
  };

  // restore deleted features
  const restoreFeature = (item: mm.Feature) => {
    if (viewer == undefined) return;
    const layer = viewer.getScene().getLayerHandler().getActiveLayer();
    if (layer === null) return;
    layer.getLayerEditor().featuresdelete = layer
      .getLayerEditor()
      .featuresdelete.filter((id: string | number) => id !== item.id);
    layer.getFeatureById(item.id).show();
    layer.reload();
    changeFeatureTableAddedRows(getChangedFeaturesList(layer));
  };

  return (
    <Box display={"flex"} flexDirection={"column-reverse"}>
      {rows !== undefined &&
        rows.map((item: mm.Feature, index: number) => (
          <React.Fragment key={item?.geometry?.id + "_" + index}>
            {item && (
              <Box
                key={item.geometry.id + "_" + index}
                display={"flex"}
                flexDirection={"column"}
              >
                <Box
                  paddingX={1}
                  paddingY={0.5}
                  display={"flex"}
                  flexDirection={"row"}
                  gap={1}
                  alignItems={"center"}
                  sx={{
                    backgroundColor:
                      index % 2 === 0
                        ? darkTheme
                          ? "primary.light" // Could this not be simplified? 3
                          : "grey.light" // Could this not be simplified? 4
                        : "",
                  }}
                >
                  <Typography
                    onClick={() => selectFeature(item)}
                    sx={{
                      "@media (hover: hover)": {
                        ":hover": {
                          cursor: "pointer",
                          textDecoration: "underline",
                        },
                      },
                    }}
                    fontSize={14}
                    fontWeight={600}
                    color={darkTheme ? "white" : "darkblue.main"}
                  >
                    {item.id
                      ? item.id
                      : t("edit_layers.new_feature") + (index + 1)}
                  </Typography>
                  {selectedTableRows !== undefined &&
                    item.id === selectedTableRows.id &&
                    editToolActivated !== "delete" && (
                      <IconButton
                        onClick={() => deleteFeature(item)}
                        size="small"
                        sx={{
                          padding: 0,
                          marginLeft: "auto",
                          color: darkTheme ? "white" : "darkblue.main",
                          "@media (hover: hover)": {
                            ":hover": { color: "secondary.main" },
                          },
                        }}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  {editToolActivated === "delete" && editPopover && (
                    <IconButton
                      onClick={() => restoreFeature(item)}
                      size="small"
                      sx={{
                        padding: 0,
                        marginLeft: "auto",
                        color: darkTheme ? "white" : "darkblue.main",
                        "@media (hover: hover)": {
                          ":hover": { color: "secondary.main" },
                        },
                      }}
                    >
                      <Delete />
                    </IconButton>
                  )}
                  <IconButton
                    onClick={() => selectFeature(item)}
                    size="small"
                    sx={{
                      padding: 0,
                      marginLeft:
                        (selectedTableRows !== undefined &&
                          item.id === selectedTableRows.id) ||
                        editToolActivated === "delete"
                          ? ""
                          : "auto",
                      color: darkTheme ? "white" : "darkblue.main",
                      "@media (hover: hover)": {
                        ":hover": { color: "secondary.main" },
                      },
                      rotate:
                        selectedTableRows !== undefined &&
                        item.id === selectedTableRows.id
                          ? "90deg"
                          : "-90deg",
                      transition: "rotate 0.2s",
                    }}
                  >
                    <ChevronLeft />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    maxHeight:
                      selectedTableRows !== undefined &&
                      item.id === selectedTableRows?.id
                        ? "200px"
                        : "0px",
                    transition: "max-height 0.3s",
                  }}
                  paddingRight={1}
                  position={"relative"}
                  overflow={"auto"}
                >
                  {selectedTableRows !== undefined &&
                    item.id === selectedTableRows?.id && (
                      <SingleFeatureTable
                        selectedTableRows={{
                          ...item.toGeoJSON(),
                          uniqueID: index,
                        }}
                      />
                    )}
                </Box>
              </Box>
            )}
          </React.Fragment>
        ))}
    </Box>
  );
}
